import React, { useState } from 'react';
import { Play, Download, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { exportToCSV } from '../../utils/export';

const s = {
  page: { padding: '2rem', maxWidth: 1200, margin: '0 auto' },
  h1: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.75rem', marginBottom: 4 },
  desc: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.9rem', marginBottom: '1.5rem', maxWidth: 700, lineHeight: 1.6 },
  btn: { fontFamily: 'DM Sans, sans-serif', background: 'var(--primary)', color: 'var(--bg)', border: 'none', padding: '10px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnOutline: { fontFamily: 'DM Sans, sans-serif', background: 'transparent', color: 'var(--secondary)', border: '1px solid var(--secondary)', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 6 },
  actions: { display: 'flex', gap: 12, marginBottom: '1.5rem', alignItems: 'center' },
  table: { width: '100%', borderCollapse: 'collapse', fontFamily: 'DM Sans, sans-serif' },
  th: { textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.1)', color: 'var(--secondary)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
  td: { padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.05)', color: 'var(--text)', fontSize: '0.88rem' },
  card: { background: 'var(--card-bg)', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem', overflowX: 'auto' },
  callout: { background: 'rgba(212,107,58,0.1)', border: '1px solid rgba(212,107,58,0.25)', borderRadius: 10, padding: '1.25rem', marginBottom: '1.5rem', fontFamily: 'DM Sans, sans-serif', color: 'var(--text)', fontSize: '0.9rem', lineHeight: 1.6 },
  calloutTitle: { fontFamily: 'Playfair Display, serif', fontSize: '1rem', color: 'var(--alert)', marginBottom: 6 },
  riskHigh: { color: 'var(--alert)', fontWeight: 600 },
  riskMed: { color: 'var(--secondary)', fontWeight: 600 },
  riskLow: { color: 'var(--primary)', fontWeight: 600 },
  spinner: { display: 'inline-block', width: 18, height: 18, border: '2px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  sortBtn: { background: 'none', border: 'none', color: 'var(--secondary)', cursor: 'pointer', fontSize: '0.7rem', marginLeft: 4 },
};

const fallbackData = [
  { name: 'David K.', tier: 'Bloom', daysSinceVisit: 24, riskScore: 0.92, riskLevel: 'High', action: 'Send win-back SMS with $3 reward' },
  { name: 'Sarah M.', tier: 'Seedling', daysSinceVisit: 28, riskScore: 0.89, riskLevel: 'High', action: 'Push notification: "We miss you" + free pastry' },
  { name: 'Alex T.', tier: 'Harvest', daysSinceVisit: 21, riskScore: 0.85, riskLevel: 'High', action: 'Personal outreach from barista team' },
  { name: 'Jordan R.', tier: 'Seedling', daysSinceVisit: 19, riskScore: 0.78, riskLevel: 'Medium', action: 'Email: seasonal menu highlights' },
  { name: 'Rachel W.', tier: 'Bloom', daysSinceVisit: 17, riskScore: 0.72, riskLevel: 'Medium', action: '"Your usual?" push reminder' },
  { name: 'Michael C.', tier: 'Seedling', daysSinceVisit: 22, riskScore: 0.70, riskLevel: 'Medium', action: 'SMS: new cold brew launch' },
  { name: 'Priya N.', tier: 'Bloom', daysSinceVisit: 15, riskScore: 0.65, riskLevel: 'Medium', action: 'Push: loyalty points expiring' },
  { name: 'James F.', tier: 'Seedling', daysSinceVisit: 14, riskScore: 0.58, riskLevel: 'Medium', action: 'Email: 2x points weekend' },
  { name: 'Lisa B.', tier: 'Harvest', daysSinceVisit: 12, riskScore: 0.42, riskLevel: 'Low', action: 'No action needed yet' },
  { name: 'Tom H.', tier: 'Bloom', daysSinceVisit: 10, riskScore: 0.35, riskLevel: 'Low', action: 'Monitor — on normal cadence' },
];

export default function ChurnRisk() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('riskScore');
  const [sortDir, setSortDir] = useState('desc');

  const runModel = async () => {
    setLoading(true);
    try {
      const res = await api.post('/models/churn');
      setResults(res.data?.results || res.data || fallbackData);
      toast.success('Churn model complete');
    } catch {
      setResults(fallbackData);
      toast.success('Churn model complete (demo data)');
    } finally {
      setLoading(false);
    }
  };

  const data = results || [];
  const sorted = [...data].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const riskStyle = (level) => level === 'High' ? s.riskHigh : level === 'Medium' ? s.riskMed : s.riskLow;

  return (
    <div style={s.page}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <h1 style={s.h1}>Churn Risk Model</h1>
      <p style={s.desc}>
        Predicts which customers are at risk of lapsing based on a 21-day inactivity threshold — the critical window for coffee habits.
        Surfaces recommended win-back actions prioritized by lifetime value and engagement history.
      </p>

      <div style={s.callout}>
        <div style={s.calloutTitle}><AlertTriangle size={16} style={{ marginRight: 6, verticalAlign: -2 }} />Remote Commuter Cohort</div>
        Customers who shifted to remote work show a distinctive pattern: their weekday visit frequency dropped 40%,
        but weekend visits increased 15%. The model flags these as "schedule-shift" rather than true churn —
        they respond best to weekend-focused push notifications with 3x the engagement rate of standard win-backs.
      </div>

      <div style={s.actions}>
        <button style={s.btn} onClick={runModel} disabled={loading}>
          {loading ? <><span style={s.spinner} /> Running...</> : <><Play size={16} /> Run Model</>}
        </button>
        {results && (
          <button style={s.btnOutline} onClick={() => exportToCSV(results, 'churn_risk')}>
            <Download size={14} /> Export CSV
          </button>
        )}
      </div>

      {results && (
        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>
                {[['name', 'Customer'], ['tier', 'Tier'], ['daysSinceVisit', 'Days Since Visit'], ['riskScore', 'Risk Score'], ['riskLevel', 'Risk Level'], ['action', 'Recommended Action']].map(([key, label]) => (
                  <th key={key} style={s.th}>
                    {label}
                    <button style={s.sortBtn} onClick={() => toggleSort(key)}>{sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr key={i}>
                  <td style={s.td}>{row.name}</td>
                  <td style={s.td}>{row.tier}</td>
                  <td style={s.td}>{row.daysSinceVisit}</td>
                  <td style={s.td}>{(row.riskScore * 100).toFixed(0)}%</td>
                  <td style={{ ...s.td, ...riskStyle(row.riskLevel) }}>{row.riskLevel}</td>
                  <td style={s.td}>{row.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
